import { cva } from "class-variance-authority";

export { default as AvatarInput } from "./AvatarInput.vue";
export { default as DateInputSelect } from "./DateInputSelect.vue";
export { default as EditableTextArea } from "./EditableTextArea.vue";
export { default as FileDropZone } from "./FileDropZone.vue";
export { default as FileInput } from "./FileInput.vue";
export { default as FileUpload } from "./FileUpload.vue";
export { default as HoursAndMinutesInput } from "./HoursAndMinutesInput.vue";
export { default as MonetaryInput } from "./MonetaryInput.vue";
export { default as PasswordInput } from "./PasswordInput.vue";
export { default as TagsInput } from "./TagsInput.vue";
export { default as TextAreaInput } from "./TextAreaInput.vue";
export { default as TextInput } from "./TextInput.vue";
export { default as GroupsInput } from "./GroupsInput.vue";
export { default as TranslatableEditableTextArea } from "./TranslatableEditableTextArea.vue";

export const sizes = ["sm", "md", "lg"];

export const inputVariants = cva(
    [
        "w-full",
        "text-primary",
        "placeholder:text-placeholder",
        "font-medium",
        "dark:bg-alpha-white-40",
        "border",
        "border-primary",
        "focus:border-brand",
        "rounded-sm",
        "focus:ring-brand-shadow",
        "focus-visible:outline-none",
        "peer",
    ],
    {
        variants: {
            size: {
                sm: ["text-sm", "h-9", "placeholder:text-sm", "px-3", "py-2"],
                md: [
                    "text-base",
                    "h-10",
                    "placeholder:text-base",
                    "px-3",
                    "py-2",
                ],
                lg: [
                    "text-base",
                    "h-11",
                    "placeholder:text-base",
                    "px-3.5",
                    "py-2.5",
                ],
            },
            disabled: {
                true: [
                    "pointer-events-none",
                    "border-disabled",
                    "text-disabled",
                    "placeholder:text-disabled",
                    "bg-disabled_subtle",
                ],
            },
            hasError: {
                true: [
                    "border-error",
                    "focus:border-error",
                    "focus:ring-error-shadow",
                ],
            },
            hasButton: {
                true: [
                    "ltr:border-r-0",
                    "ltr:rounded-r-none",
                    "rtl:border-l-0",
                    "rtl:rounded-l-none",
                ],
            },
        },
        compoundVariants: [
            {
                size: "sm",
                hasStartSlot: true,
                class: ["ltr:pl-8", "rtl:pr-8"],
            },
            {
                size: "md",
                hasStartSlot: true,
                class: ["ltr:pl-8", "rtl:pr-8"],
            },
            {
                size: "lg",
                hasStartSlot: true,
                class: ["ltr:pl-10", "rtl:pr-10"],
            },
            {
                size: "sm",
                hasEndSlot: true,
                class: ["ltr:pr-8", "rtl:pl-8"],
            },
            {
                size: "md",
                hasEndSlot: true,
                class: ["ltr:pr-8", "rtl:pl-8"],
            },
            {
                size: "lg",
                hasEndSlot: true,
                class: ["ltr:pr-10", "rtl:pl-10"],
            },
        ],
    }
);

export const variants = cva(
    [
        "flex",
        "flex-col",
        "grow",
        "gap-1.5",
        "disabled:pointer-events-none",
        "disabled:cursor-not-allowed",
        "text-primary",
        "font-medium",
        "relative",
        "[&>div]:flex",
        "[&>div]:items-center",
        "[&>div]:relative",
        "[&_.start]:absolute",
        "[&_.start]:flex",
        "[&_.start]:items-center",
        "[&_.start]:fg-quinary",
        "[&_.end]:absolute",
        "[&_.end]:flex",
        "[&_.end]:items-center",
        "[&_.end]:fg-quinary",
    ],
    {
        variants: {
            size: {
                sm: [
                    "text-style-xs",
                    "ltr:[&_.start]:left-3",
                    "ltr:[&_.end]:right-3",
                    "rtl:[&_.start]:right-3",
                    "rtl:[&_.end]:left-3",
                    "[&_.message]:text-style-xs",
                ],
                md: [
                    "text-style-sm",
                    "ltr:[&_.start]:left-3",
                    "ltr:[&_.end]:right-3",
                    "rtl:[&_.start]:right-3",
                    "rtl:[&_.end]:left-3",
                    "[&_.message]:text-style-sm",
                ],
                lg: [
                    "text-style-sm",
                    "ltr:[&_.start]:left-3.5",
                    "ltr:[&_.end]:right-3.5",
                    "rtl:[&_.start]:right-3.5",
                    "rtl:[&_.end]:left-3.5",
                    "[&_.message]:text-style-sm",
                ],
            },
            hasError: {
                true: [
                    "[&_.start]:fg-error-secondary",
                    "[&_.end]:fg-error-secondary",
                    "[&_.message]:text-error-primary",
                ],
            },
        },
    }
);

export const buttonVariants = cva(
    [
        "flex",
        "items-center",
        "justify-center",
        "grow",
        "py-2",
        "px-[11px]",
        "border",
        "border-primary",
        "ltr:rounded-l-none",
        "ltr:rounded-r-sm",
        "rtl:rounded-r-none",
        "rtl:rounded-l-sm",
        "hover:cursor-pointer",
    ],
    {
        variants: {
            size: {
                sm: ["h-9"],
                md: ["h-10"],
                lg: ["h-11"],
            },
        },
    }
);

export const editableTextAreaVariants = cva(
    [
        "relative",
        "max-w-full",
        "w-full",
        "flex",
        "flex-col",
        "p-0",
        "whitespace-pre-wrap",
        "break-words",
    ],
    {
        variants: {
            variant: {
                description: [
                    "px-4",
                    "py-3",
                    "bg-primary",
                    "border",
                    "border-secondary",
                    "rounded-md",
                ],
                badge: [
                    "whitespace-nowrap",
                    "py-0.5",
                    "ltr:has-data-empty:has-data-placeholder-shown:pl-5",
                    "rtl:has-data-empty:has-data-placeholder-shown:pr-5",
                    "bg-primary",
                    "border",
                    "border-primary",
                    "has-data-empty:has-data-placeholder-shown:border-dashed",
                    "has-data-focused:border-dashed",
                    "rounded-sm",
                    "[&_i]:hidden",
                    "[&_i]:has-data-empty:has-data-placeholder-shown:block",
                    "[&_i]:fg-quaternary",
                    "hover:cursor-pointer",
                ],
            },
        },
        compoundVariants: [
            {
                variant: "title",
                readonly: false,
                class: ["hover:bg-secondary"],
            },
            {
                variant: "badge",
                size: "sm",
                class: ["h-5.5", "px-1.5", "[&_i]:w-3.5"],
            },
            {
                variant: "badge",
                size: "md",
                class: ["h-6", "px-2"],
            },
        ],
        defaultVariants: {
            variant: "default",
            size: "default",
            readonly: false,
        },
    }
);

export const imageTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
];
